//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import _ from 'lodash';

import NumberUtils from 'common/utils/NumberUtils';
import { EnhancedField } from 'common/components/core/form/Field';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';
import Loader from 'common/components/core/loader/Loader';
import { dashboardColors } from 'common/enums/ColorEnum';
import { Icon } from 'common/components/core/icon';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';
import { Button, ButtonTypes } from 'common/components/core/button';
import coreIds from 'common/tests/core/coreIds.json';
import theme from './CustomEditableField.scss';

type Props = {
    theme: Object,
    className?: string,
    overlayIcon?: Icon,
    value: string,
    error: string,
    leftIcon: string,
    popOverPosition?: string,
    onReject: Function,
    updateValue: (value: string) => void,
    testId?: string
};

export class ActiveFieldComponent extends React.Component<Props, null> {
    handleChange = value => {
        const { updateValue } = this.props;
        updateValue && updateValue(value);
    };

    render() {
        const {
            theme,
            className,
            value,
            error,
            leftIcon,
            placeholder,
            popOverPosition,
            ...others
        } = this.props;
        return (
            <EnhancedField
                {...others}
                place={popOverPosition || 'right'}
                element={'input'}
                autoFocus
                value={value}
                defaultValue={value}
                error={error}
                className={className}
                theme={theme}
                onChange={this.handleChange}
                leftIcon={leftIcon}
                placeholder={placeholder}
            />
        );
    }
}

export const InactiveFieldComponent = ({
    theme,
    value,
    displayValue,
    placeholder
}) => {
    const valueToShow = displayValue || value;
    return (
        <div
            className={classnames(theme.customFieldContainer, {
                [theme.placeholder]: !valueToShow
            })}
        >
            <p title={valueToShow}>
                {valueToShow || NumberUtils.isNumeric(valueToShow)
                    ? valueToShow
                    : placeholder}
            </p>
        </div>
    );
};

const defaults = {
    ActiveFieldComponent,
    InactiveFieldComponent
};

const editableComponentFactory = ActiveFieldComponent => {
    return class EditableFieldState extends React.Component {
        // prevent save on enter press on cross button
        handleKeyDownOnCancel = e => {
            if (e.keyCode === 13) {
                e.stopPropagation();
            }
        };

        render() {
            const {
                theme,
                errorMessage,
                isLoading,
                value,
                onSave,
                onBulkSave,
                onReject,
                updateValue,
                disableIcons,
                hideDropdownIndicator,
                enableBulkEditing,
                ...others
            } = this.props;
            return (
                <div className={classnames(theme.editableField)}>
                    <ActiveFieldComponent
                        {...others}
                        onChange={updateValue}
                        theme={theme}
                        error={errorMessage}
                        disabled={isLoading}
                        hideDropdownIndicator={
                            hideDropdownIndicator || isLoading
                        }
                        value={value}
                        className={classnames(theme.customInputField, {
                            [theme.error]: errorMessage
                        })}
                        updateValue={updateValue}
                    />
                    <Loader
                        active={isLoading}
                        theme={theme}
                        backgroundColor={dashboardColors.dark.name}
                    />
                    {!isLoading && !disableIcons ? (
                        <div className={theme.saveOptions}>
                            <Button
                                data-test-id={coreIds.editableField.save}
                                theme={theme}
                                className={theme.saveBtn}
                                color={dashboardColors.paleGreySeven.name}
                                icon={'tickmark'}
                                onClick={onSave}
                                disabled={!value}
                                buttonType={ButtonTypes.Icon}
                            />
                            {enableBulkEditing && (
                                <Button
                                    theme={theme}
                                    className={theme.editAllBtn}
                                    color={dashboardColors.paleGreySeven.name}
                                    icon={'double-tick'}
                                    onClick={onBulkSave}
                                    buttonType={ButtonTypes.Icon}
                                />
                            )}
                            <Button
                                data-test-id={coreIds.editableField.reject}
                                theme={theme}
                                className={theme.cancelBtn}
                                color={dashboardColors.paleGreySeven.name}
                                icon={'cancel-heavy'}
                                onClick={onReject}
                                onKeyDown={this.handleKeyDownOnCancel}
                                buttonType={ButtonTypes.Icon}
                            />
                        </div>
                    ) : null}
                </div>
            );
        }
    };
};

const customEditableFieldFactory = ({
    ActiveFieldComponent = defaults.ActiveFieldComponent,
    InactiveFieldComponent = defaults.InactiveFieldComponent
}) => {
    const EditableFieldComponent = editableComponentFactory(
        ActiveFieldComponent
    );
    const EnhancedEditableFieldState = HandleOutsideClick(
        KeyEventHandler(EditableFieldComponent)
    );
    class EditableField extends React.Component {
        state = {
            isActive: this.props.defaultIsActive ? true : false,
            isLoading: false,
            errorMessage: null,
            value: null
        };

        UNSAFE_componentWillReceiveProps(nextProps) {
            const {
                value: nextValue,
                forceInactiveState: nextForceInactiveState
            } = nextProps;
            const {
                value,
                type,
                forceInactiveState,
                onSetInactiveState
            } = this.props;
            // we need to keep the active field diabled till the data is loading
            // and only after that we need to change back to the inactive component

            const success =
                type === 'number'
                    ? this.numericCheck(nextValue, this.state.value, value)
                    : this.isUpdateSuccessful(
                          nextValue,
                          this.state.value,
                          value
                      );

            if (success) {
                this.setState({ isActive: false });
            }

            if (
                nextForceInactiveState !== forceInactiveState &&
                nextForceInactiveState
            ) {
                this.setInactiveState();
                onSetInactiveState();
            }
        }

        //remove real soon.
        numericCheck = (value1, value2, value3) => {
            const nextValue = NumberUtils.isNumeric(value1)
                ? NumberUtils.toNumber(value1)
                : value1;
            const currentStateValue = NumberUtils.isNumeric(value2)
                ? NumberUtils.toNumber(value2)
                : value2;
            const previousValue = NumberUtils.isNumeric(value3)
                ? NumberUtils.toNumber(value3)
                : value3;
            if (value3) {
                return this.isUpdateSuccessful(
                    nextValue,
                    currentStateValue,
                    previousValue
                );
            }

            return nextValue === currentStateValue;
        };

        isUpdateSuccessful = (nextValue, currentStateValue, previousValue) =>
            _.isEqual(nextValue, currentStateValue) &&
            !_.isEqual(nextValue, previousValue) &&
            this.state.isActive;

        setActiveState = e => {
            const { value, isEditingEnabled } = this.props;
            e.preventDefault();
            e.stopPropagation();
            if (!this.state.isActive && isEditingEnabled) {
                this.setState({
                    isActive: true,
                    value
                });
            }
        };

        setInactiveState = () => {
            const { isEditingEnabled } = this.props;
            if (this.state.isActive && isEditingEnabled) {
                this.setState({
                    isActive: false
                });
            }
        };

        updateValue = val => {
            const { onChange, getProcessedValue } = this.props;
            const value = getProcessedValue ? getProcessedValue(val) : val;
            this.setState({ value, errorMessage: null });
            onChange && onChange(value);
        };

        onSave = e => {
            e.preventDefault();
            e.stopPropagation();
            const { value } = this.state;
            const {
                onSave,
                value: propValue,
                type,
                disableSaveOnError
            } = this.props;
            // If same value, no need to send a request else make an api call
            const errorMessage = this.getErrorMessage();
            if (errorMessage && disableSaveOnError) {
                return;
            }
            if (
                _.isEqual(value, propValue) ||
                (type === 'number' && this.numericCheck(value, propValue))
            ) {
                this.setState({
                    isActive: false,
                    errorMessage: null
                });
                return;
            }
            this.setState({ isLoading: true, errorMessage: null });
            onSave(value)
                .then(() => {
                    this.setState({ isLoading: false, isActive: false });
                })
                .catch(error => {
                    this.setState({
                        errorMessage: error.message,
                        isLoading: false
                    });
                });
        };

        onBulkSave = e => {
            e.preventDefault();
            e.stopPropagation();
            const { value } = this.state;
            const { onBulkSave } = this.props;
            this.setState({ isLoading: true, errorMessage: null });
            onBulkSave(value)
                .then(() => {
                    this.setState({ isLoading: false, isActive: false });
                })
                .catch(error => {
                    this.setState({
                        errorMessage: error.message,
                        isLoading: false
                    });
                });
        };

        onReject = e => {
            e.preventDefault();
            e.stopPropagation();
            const { onReject } = this.props;
            this.setState({ isActive: false, value: null, errorMessage: null });
            return onReject && onReject();
        };

        getErrorMessage() {
            return this.props.errorMessage || this.state.errorMessage;
        }

        render() {
            const {
                theme,
                isEditingEnabled,
                customFieldClass,
                editIconClass,
                disabledEditTooltip,
                onSave, // eslint-disable-line no-unused-vars
                onReject, // eslint-disable-line no-unused-vars
                disableIcons,
                disableEditIcon,
                saveOnEnter = true,
                getExtraContainer,
                className,
                autoSaveOnOutsideClick = true,
                activateOnFieldClick = true,
                testId,
                ...others
            } = this.props;
            const { value, isLoading, isActive } = this.state;
            const errorMessage = this.getErrorMessage();
            return (
                <DivWithPopover
                    data-test-id={testId || coreIds.editableField.main}
                    className={classnames(theme.customField, className, {
                        [theme.isEditingEnabled]: isEditingEnabled,
                        [theme.isInactive]: !isActive,
                        [theme.isActive]: isActive,
                        [customFieldClass]: customFieldClass
                    })}
                    popOverContent={
                        !isEditingEnabled ? disabledEditTooltip : null
                    }
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    onClick={
                        isEditingEnabled && activateOnFieldClick
                            ? this.setActiveState
                            : null
                    }
                >
                    {isActive ? (
                        <EnhancedEditableFieldState
                            {...others}
                            getExtraContainer={getExtraContainer}
                            disableIcons={disableIcons}
                            handleClickOutside={
                                autoSaveOnOutsideClick
                                    ? this.onSave
                                    : this.onReject
                            }
                            onEscape={this.onReject}
                            onEnter={saveOnEnter ? this.onSave : null}
                            ActiveFieldComponent={ActiveFieldComponent}
                            theme={theme}
                            onSave={this.onSave}
                            onBulkSave={this.onBulkSave}
                            onReject={this.onReject}
                            updateValue={this.updateValue}
                            value={value}
                            isLoading={isLoading}
                            errorMessage={errorMessage}
                        />
                    ) : (
                        <div className={classnames(theme.editableField)}>
                            <InactiveFieldComponent
                                {...others}
                                isEditingEnabled={isEditingEnabled}
                                theme={theme}
                                className={className}
                            />
                            {!disableEditIcon && !disableIcons ? (
                                <Icon
                                    theme={theme}
                                    className={theme.overlayEditIcon}
                                    icon={
                                        editIconClass
                                            ? editIconClass
                                            : 'edit-icon'
                                    }
                                />
                            ) : null}
                        </div>
                    )}
                </DivWithPopover>
            );
        }
    }
    return themr('CustomEditableField', theme, {
        withRef: true
    })(EditableField);
};

const EditableInputField = customEditableFieldFactory({});
export { customEditableFieldFactory, EditableInputField };
